import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { useSelector } from 'react-redux'

import { Layout } from 'components/layout'
import { useEventType, useEventTypeContext } from 'features/event_type_provider'
import { isAdminOrOwnerOrGroupAdminSelector } from 'selectors/current_user'
import { useETEditorFormikPoolingType } from 'services/event_types'

import { PanelContentContainer } from '../styles'
import { AddHostButton } from './components/add_host_button'
import { CoHostsAndUserPools } from './components/co_hosts_and_user_pools'
import {
  CreateUserPoolButton,
  HostListItem,
  ReschedulePreference,
  UserPoolSidePanel,
  UserPoolSidePanelProvider
} from './components/round_robin'
import { SingleHostSelect } from './components/single_host_select'
import { useUserPoolsData } from './hooks'
import { useEnabledHosts } from '../../../hooks'

const Actions = (props) => <Layout relative gap="8" {...props} />

const SingleHost = ({ isLocked }) => {
  const { eventType, isCreation } = useEventTypeContext()
  const enabledHosts = useEnabledHosts()
  const isAdminOrOwnerOrGroupAdmin = useSelector(isAdminOrOwnerOrGroupAdminSelector)
  const { isTeam, isGroup } = eventType

  const canSelectHost = !isLocked && (
    (isCreation && isAdminOrOwnerOrGroupAdmin) || (isTeam && isGroup)
  )

  if (canSelectHost) return <SingleHostSelect eventType={eventType} />

  return (
    <HostListItem
      isLocked
      member={enabledHosts[0]}
      eventType={eventType}
    />
  )
}

const MultiHosts = ({ isLocked }) => {
  const eventType = useEventType()
  const { isSubmitting } = useFormContext()
  const { isTeam } = eventType

  const {
    collectiveUserPoolUid,
    hasCollectiveUserPools,
    isRoundRobin,
    hasOnlyRoundRobinUserPool,
  } = useUserPoolsData()

  const show = {
    addCollectiveHostButton: !isLocked && (!isTeam || hasCollectiveUserPools),
    addRoundRobinPoolButton: !isLocked && !isTeam,
    reschedulePreference: hasOnlyRoundRobinUserPool || isRoundRobin,
  }

  return (
    <>
      <PanelContentContainer section>
        <UserPoolSidePanelProvider>
          {(show.addCollectiveHostButton || show.addRoundRobinPoolButton) && (
            <Actions>
              {show.addCollectiveHostButton && (
                <AddHostButton
                  eventType={eventType}
                  userPoolUid={collectiveUserPoolUid}
                >
                  {I18n.t('publisher.event_type_editor.sections.hosts.collective.add_host')}
                </AddHostButton>
              )}
              {show.addRoundRobinPoolButton && (
                <CreateUserPoolButton />
              )}
            </Actions>
          )}
          <CoHostsAndUserPools eventType={eventType} isLocked={isLocked} />
          <UserPoolSidePanel eventType={eventType} isLocked={isLocked} />
        </UserPoolSidePanelProvider>
      </PanelContentContainer>
      {show.reschedulePreference && (
        <ReschedulePreference
          disabled={isSubmitting}
          isLocked={isLocked}
        />
      )}
    </>
  )
}

export const Hosts = ({ isReadOnly = false, isLocked = false }) => {
  const { isMultiHost } = useETEditorFormikPoolingType()

  const HostsComponent = isMultiHost ? MultiHosts : SingleHost

  return (
    <PanelContentContainer>
      <HostsComponent isLocked={isLocked || isReadOnly} />
    </PanelContentContainer>
  )
}
